import { useMutation } from "@apollo/client/react";
import { useAuth, useUser } from "@/app/providers";
import { useCallback } from "react";
import { ME, UPDATE_CURRENT_USER_PROFILE } from "@/hooks";
import type { MutationUpdateUserAdminArgs } from "@/types/graphql";
import type { Dispatch, SetStateAction } from "react";

const { VITE_API_URL_GRAPHQL } = import.meta.env;

type UpdateProfileVariables = Partial<MutationUpdateUserAdminArgs> & {
  file?: File | null;
};

type UserLike = Record<string, unknown>;

/**
 * Custom hook to update the user profile.
 * @param {Function} [onCompleted] - Callback function to be called when the mutation completes successfully.
 * @param {Function} [onError] - Callback function to be called when an error occurs during the mutation.
 * @returns {{updateProfile: Function, loading: boolean}} - The function to update the profile and the loading state.
 */
export function useUpdateProfile(
  onCompleted: (data?: any) => void = () => {},
  onError: (error?: any) => void = () => {},
) {
  const { setUser } = useUser() as {
    setUser: Dispatch<SetStateAction<UserLike | null | undefined>>;
  };
  const { token } = useAuth();

  const [comUpdateAccount, { loading }] = useMutation<any>(
    UPDATE_CURRENT_USER_PROFILE,
    {
      onCompleted: (data: any) => {
        const comUpdateAccount = data?.updateAccount || data?.comUpdateAccount;
        if (!comUpdateAccount?.success) {
          const errorMessage = comUpdateAccount?.message;
          const errorText = Array.isArray(errorMessage)
            ? errorMessage[0]
            : errorMessage || "Update failed";
          (onError as any)(new Error(errorText));
          return;
        }
        setUser((prev: UserLike | null | undefined) => {
          return {
            ...prev,
            ...comUpdateAccount?.user,
          };
        });
        (onCompleted as any)(comUpdateAccount);
      },
      onError,
    },
  );

  const updateProfile = useCallback(
    async (variables: UpdateProfileVariables) => {
      // If there's a file, use fetch with FormData following GraphQL multipart request spec
      if (variables.file) {
        try {
          // Build the operations object with the mutation and variables
          const operations = {
            query: `mutation updateAccount($name: String, $file: Upload, $countryCode: String, $lang: String, $timezone: String) {
              updateAccount(name: $name, countryCode: $countryCode, lang: $lang, timezone: $timezone, file: $file) {
                success
                message
                user {
                  id
                  name
                  email
                  customerKey
                  lang
                  profilePhotoPath
                  profilePhotoUrl
                  countryCode
                  timezone
                  isSuperAdmin
                  roles
                  isVerified
                  isActive
                  currentTeam
                  currentTeamId
                  currentSubsidiary
                  currentSubsidiaryId
                  currentTenantId
                  currentPartnerId
                  currentRoleKey
                  status
                  lastLogin
                  createdAt
                  updatedAt
                  permissions {
                    role
                    modules
                  }
                  partner {
                    id
                    key
                    name
                    companyUrl
                    appUrl
                    createdBy
                    updatedBy
                    createdAt
                    updatedAt
                  }
                }
              }
            }`,
            variables: {
              name: variables.name || null,
              file: null as File | null,
              countryCode: variables.countryCode || null,
              lang: variables.lang || null,
              timezone: variables.timezone || null,
            },
          };

          // Build the map to indicate which variable path the file should be placed at
          const map = {
            "0": ["variables.file"],
          };

          // Build multipart form data following GraphQL multipart request spec
          const formData = new FormData();
          formData.append("operations", JSON.stringify(operations));
          formData.append("map", JSON.stringify(map));
          formData.append("0", variables.file);

          // Build headers with authorization token
          // Note: Don't set Content-Type header, let the browser set it with the boundary
          const headers = {};
          if (token) {
            (headers as any).authorization = `Bearer ${token}`;
          }

          // Use fetch to upload
          const response = await fetch(VITE_API_URL_GRAPHQL, {
            method: "POST",
            headers: headers,
            credentials: "include",
            body: formData,
          });

          const result = await response.json();

          if (result.errors) {
            throw new Error(result.errors[0]?.message || "Update failed");
          }

          const accountRes =
            result.data?.updateAccount || result.data?.comUpdateAccount;
          if (!accountRes?.success) {
            const errorMessage = accountRes?.message;
            throw new Error(
              Array.isArray(errorMessage)
                ? errorMessage[0]
                : errorMessage || "Update failed",
            );
          }

          // Update user state
          setUser((prev: UserLike | null | undefined) => {
            return {
              ...prev,
              ...accountRes?.user,
            };
          });

          (onCompleted as any)(accountRes);
        } catch (err) {
          (onError as any)(err);
        }
      } else {
        // No file, use regular mutation
        const { file: _file, ...cleanVariables } = variables;
        comUpdateAccount({
          variables: cleanVariables,
          refetchQueries: [{ query: ME }],
        }).then();
      }
    },
    [comUpdateAccount, setUser, token, onCompleted, onError],
  );

  return {
    updateProfile,
    loading,
  };
}
